
import { useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"

import { JobWidget } from "../Offerspage/JobWidgets"

/**
 * Horizontal use-case carousel: one card per industry, CSS scroll-snap (swipe on phones, arrow buttons on
 * desktop). No autoplay. Each card = industry tag, the WhatsApp pieces for that job, one outcome line.
 */
export function UseCaseCarousel({
  items,
  label,
  prevLabel,
  nextLabel,
  swipeHint,
}) {
  const track = useRef(null)
  const [edge, setEdge] = useState({ start: true, end: false })

  const update = () => {
    const el = track.current
    if (!el) return

    setEdge({
      start: el.scrollLeft <= 4,
      end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 4,
    })
  }

  useEffect(() => {
    const el = track.current
    if (!el) return

    el.scrollTo({ left: 0 })
    update()

    const ro = new ResizeObserver(update)
    ro.observe(el)

    return () => ro.disconnect()
  }, [items])

  const page = (dir) => {
    const el = track.current
    if (!el) return

    const card = el.querySelector("li")
    const step = card
      ? card.getBoundingClientRect().width + 16
      : el.clientWidth * 0.8

    el.scrollBy({ left: dir * step, behavior: "smooth" })
  }

  return (
    <div
      className="relative flex flex-col gap-4"
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
    >
      <ul
        ref={track}
        onScroll={update}
        tabIndex={0}
        className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-2 [scrollbar-width:none] focus-visible:outline-2 focus-visible:outline-ring sm:mx-0 sm:scroll-px-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
      >
        {items.map((u, i) => (
          <li
            key={`${u.industry}-${i}`}
            aria-label={`${i + 1} of ${items.length}: ${u.industry}`}
            className="animate-node-in flex w-[84%] shrink-0 snap-start flex-col gap-4 rounded-2xl border border-border bg-surface-soft p-5 sm:w-[46%] lg:w-[31.5%]"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="rounded-md bg-charcoal px-2.5 py-1 text-xs font-semibold text-white">
                {u.industry}
              </span>

              {u.job && (
                <span className="text-xs font-semibold text-brand-text">
                  {u.job}
                </span>
              )}
            </div>

            <div className="flex flex-1 flex-col gap-2.5">
              {u.widgets.map((w, k) => (
                <JobWidget
                  key={k}
                  w={w}
                  className={w.kind === "out" ? "self-end" : undefined}
                />
              ))}
            </div>

            <p className="border-t border-border pt-3 text-sm font-medium text-ink-strong">
              {u.outcome}
            </p>
          </li>
        ))}
      </ul>

      <p
        className="text-center text-xs font-medium text-muted-foreground sm:hidden"
        aria-hidden="true"
      >
        {swipeHint} →
      </p>

      <div className="flex justify-end gap-2 max-sm:hidden">
        <button
          type="button"
          onClick={() => page(-1)}
          disabled={edge.start}
          aria-label={prevLabel}
          className="flex size-11 items-center justify-center rounded-full border border-border bg-background shadow-card disabled:opacity-40"
        >
          <ChevronLeftIcon className="size-5" aria-hidden="true" />
        </button>

        <button
          type="button"
          onClick={() => page(1)}
          disabled={edge.end}
          aria-label={nextLabel}
          className="flex size-11 items-center justify-center rounded-full border border-border bg-background shadow-card disabled:opacity-40"
        >
          <ChevronRightIcon className="size-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
